import './style.css'

import {
  getMiddleCenterBias,
  GridCellKind,
  TextCellEntry,
  type CustomCell,
  type CustomRenderer,
  type ProvideEditorCallback,
} from '@glideapps/glide-data-grid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { IconColOptionsOnly } from '@makyoapp/makyo-common'

import { IconCell } from '@/core/models/columns/icon'

import { getEditorStyle } from '../helpers/editor-style-helper'
import { IconEditor } from './IconEditor'

interface CustomIconCellProps {
  readonly type: ColumnTypes
  readonly cell: IconCell
  readonly settings: IconColOptionsOnly
}

export type CustomIconCell = CustomCell<CustomIconCellProps>

const Editor: ReturnType<ProvideEditorCallback<CustomIconCell>> = ({ value, onFinishedEditing }) => {
  const cell = value.data.cell

  if (value.readonly) {
    return (
      <TextCellEntry
        highlight={true}
        autoFocus={false}
        disabled={true}
        value={cell.toString() ?? ''}
        onChange={() => undefined}
      />
    )
  }

  return (
    <IconEditor
      value={cell.toString() ?? ''}
      onChange={(icon) => {
        if (icon === undefined) return

        let updatedCell = cell.toCell(icon) as IconCell

        onFinishedEditing({
          ...value,
          data: {
            ...value.data,
            type: ColumnTypes.Icon,
            cell: {
              ...cell,
              value: icon,
              toCell: updatedCell.toCell,
            },
          },
        })
      }}
      onCloseButtonClick={() => {
        onFinishedEditing(undefined)
      }}
    />
  )
}

const CustomIconRenderer: CustomRenderer<CustomIconCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is CustomIconCell => (cell.data as CustomIconCellProps).type === ColumnTypes.Icon,
  draw: async (args, cell) => {
    const cellValue = cell.data.cell.toString()
    if (cellValue === '[object Object]') return

    drawIcon(args, cellValue ?? '')
  },
  provideEditor: () => ({
    disablePadding: true,
    disableStyling: false,
    styleOverride: getEditorStyle(434),
    editor: Editor,
  }),
}

export const drawIcon = (args: any, icon: string) => {
  const { ctx, theme, rect } = args
  ctx.fillStyle = theme.textDark
  ctx.font = '24px Material Symbols Rounded'
  ctx.fillText(
    icon,
    rect.x + theme.cellHorizontalPadding,
    rect.y + rect.height / 2 + getMiddleCenterBias(ctx, ctx.font)
  )
}

export default CustomIconRenderer
